import React, { FormEvent, useEffect, useState } from 'react';
import { CcIcon } from '@utp/components-react';
import {
  WrapperVerificationCode,
  WrapperVerificationCodeInputs,
  WrapperVerificationCodeMessage,
  WrapperVerificationCodeSpaces
} from './styled';

interface IProps {
  className?: string;
  setValue: (value: string) => void;
  status?: 'DEFAULT' | 'ERROR' | 'FINISH' | 'SUCCESS';
}

const VerificationCode = ({ className, status, setValue }: IProps) => {
  const [message, setMessage] = useState('');
  const [isDisabled, setIsDisabled] = useState(false);
  const [values, setValues] = useState({
    value1: '',
    value2: '',
    value3: '',
    value4: ''
  });

  const variants = {
    DEFAULT: { icon: '' },
    ERROR: { icon: 'x' },
    FINISH: { icon: 'x' },
    SUCCESS: { icon: 'check' }
  };

  const onInput = (e: FormEvent<HTMLInputElement>, name: string) => {
    const target = e.target as HTMLInputElement;
    const nativeEvent = e.nativeEvent as InputEvent;

    const nextInput = target.nextSibling as HTMLInputElement;
    const prevInput = target.previousSibling as HTMLInputElement;

    const value = target.value.replace(/\D/g, '');

    setValues((prev) => ({
      ...prev,
      [name]: value.slice(value.length - 1, value.length)
    }));

    if (nativeEvent.inputType === 'deleteContentBackward') {
      if (prevInput) prevInput.focus();
      else target.blur();
    }

    if (nativeEvent.inputType === 'insertText') {
      if (nextInput) nextInput.focus();
      else target.blur();
    }
  };

  const onReset = () => {
    const input = document.querySelector('[data-initial]') as HTMLInputElement;
    if (input) input.focus();

    setValues({
      value1: '',
      value2: '',
      value3: '',
      value4: ''
    });
    setValue('');
  };

  useEffect(() => {
    const arr = Object.values(values);
    const arrFilter = arr.filter((v) => v !== '');

    if (arrFilter.length === 4) setValue(arrFilter.join(''));
  }, [values]);

  useEffect(() => {
    setIsDisabled(false);
    setMessage('');

    if (status === 'ERROR') {
      setMessage('Código y/o contraseña incorrectos.');
    }

    if (status === 'FINISH') {
      setMessage('Debes ingresar el código.');
      setValues({
        value1: '',
        value2: '',
        value3: '',
        value4: ''
      });
    }

    if (['FINISH', 'SUCCESS'].includes(status || '')) {
      setIsDisabled(true);
    }
  }, [status]);

  return (
    <WrapperVerificationCode className={className}>
      <WrapperVerificationCodeSpaces status={status}>
        <WrapperVerificationCodeInputs status={status}>
          <input
            data-initial
            data-testid="verification-code-input"
            disabled={isDisabled}
            onClick={() => onReset()}
            onInput={(e) => onInput(e, 'value1')}
            type="text"
            value={values.value1}
          />
          <input
            disabled={isDisabled}
            data-testid="verification-code-input"
            onClick={() => onReset()}
            onInput={(e) => onInput(e, 'value2')}
            type="text"
            value={values.value2}
          />
          <input
            disabled={isDisabled}
            data-testid="verification-code-input"
            onClick={() => onReset()}
            onInput={(e) => onInput(e, 'value3')}
            type="text"
            value={values.value3}
          />
          <input
            disabled={isDisabled}
            data-testid="verification-code-input"
            onClick={() => onReset()}
            onInput={(e) => onInput(e, 'value4')}
            type="text"
            value={values.value4}
          />
        </WrapperVerificationCodeInputs>
        {['ERROR', 'FINISH', 'SUCCESS'].includes(status ?? '') && (
          <button disabled={isDisabled} type="button" onClick={() => onReset()}>
            <CcIcon size={16} name={status && variants[status]?.icon} />
          </button>
        )}
      </WrapperVerificationCodeSpaces>
      {message.length > 0 && (
        <WrapperVerificationCodeMessage>
          <CcIcon size={16} name="alert-triangle" />
          <span>{message}</span>
        </WrapperVerificationCodeMessage>
      )}
    </WrapperVerificationCode>
  );
};

export default VerificationCode;
